import styled, { css } from 'styled-components/macro';
import { ButtonBase } from '@mui/material';

/* ─────────────────────────  outer wrapper  ────────────────────────────── */

export const Container = styled.div<{
  margin: string;
  children?: React.ReactNode;
}>`
  display: inline-flex;
  margin: ${({ margin }) => margin};
`;

/* ─────────────────────────────  MENU  ─────────────────────────────────── */

export const MenuButton = styled(ButtonBase)<{
  $active: boolean;
  size: number;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children?: React.ReactNode;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  border-radius: 10px;

  ${({ theme, $active }) => css`
    filter: grayscale(${$active ? 0 : 1})
      brightness(
        ${$active
          ? theme.menu.active.brightness
          : theme.menu.inActive.brightness}
      )
      contrast(
        ${$active
          ? theme.menu.active.contrast
          : theme.menu.inActive.contrast}
      );

    &:hover {
      filter: grayscale(0) brightness(${theme.menu.active.brightness})
        contrast(${theme.menu.active.contrast});
    }
  `}
`;

/* ─────────────────────────────  TEXT  ─────────────────────────────────── */

export const TextButton = styled.div<{
  $active: boolean;
  role?: string;
  width: number;
  height: number;
  isMuted?: boolean;
  isWarning?: boolean;
  disabled?: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children?: React.ReactNode;
}>`
  display: flex;
  justify-content: center;
  align-items: center;

  /* Legacy spacing */
  margin: 2px 4px;

  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;

  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.palette.textSecondary};

  /* ── colour logic identical to old TextButton ───────────────────────── */
  ${({ theme, isWarning, isMuted, disabled }) => {
    const bg = isWarning
      ? theme.palette.accentPrimary          /* red */
      : disabled
      ? '#999999'
      : isMuted
      ? theme.palette.accentSecondary
      : theme.palette.backgroundQuaternary; /* dark blue */

    const textCol = isWarning ? 'white' : theme.palette.textPrimary;

    const hoverBg = isWarning
      ? theme.palette.accentPrimary         /* stays red */
      : disabled
      ? '#999999'
      : isMuted
      ? theme.palette.accentSecondary
      : theme.palette.backgroundTertiary;   /* lighter blue */

    return css`
      background-color: ${bg};
      color: ${textCol};
      opacity: ${disabled ? 0.65 : 1};
      cursor: ${disabled ? 'not-allowed' : 'pointer'};

      &:hover {
        background-color: ${hoverBg};
        border-color: ${theme.palette.textPrimary};
      }
    `;
  }}

  /* thin outline when active (optional) */
  ${({ $active, theme }) =>
    $active &&
    css`
      box-shadow: 0 0 0 2px ${theme.palette.accentSecondary};
    `}
`;

export const Contents = styled.div<{
  disabled?: boolean;
  children?: React.ReactNode;
}>`
  display: flex;
  align-items: center;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'all')};
`;

/* icon inherits colour + legacy brightness/contrast animation */
export const IconBox = styled.div<{
  $disabled?: boolean;
  $active?: boolean;
  children?: React.ReactNode;
}>`
  display: flex;
  align-items: center;
  color: inherit;

  ${({ theme, $disabled, $active }) => css`
    filter: grayscale(${$disabled ? 1 : $active ? 0 : 1})
      brightness(
        ${$disabled
          ? theme.menu.disabled.brightness
          : $active
          ? theme.menu.active.brightness
          : theme.menu.inActive.brightness}
      )
      contrast(
        ${$disabled
          ? theme.menu.disabled.contrast
          : $active
          ? theme.menu.active.contrast
          : theme.menu.inActive.contrast}
      );
  `}

  /* on hover, match “active” style */
  ${TextButton}:hover & {
    ${({ theme, $disabled }) =>
      !$disabled &&
      css`
        filter: grayscale(0) brightness(${theme.menu.active.brightness})
          contrast(${theme.menu.active.contrast});
      `}
  }

  svg {
    fill: currentColor;
    stroke: currentColor;
  }
`;

export const Label = styled.span<{
  fontSize: number;
  children?: React.ReactNode;
}>`
  padding: 0 5px;
  font-size: ${({ fontSize }) => fontSize}px;
  white-space: nowrap;
`;
